import { useHistory } from 'react-router-dom';
import { useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';
import Table from 'components/Table';
import PageContainer from 'components/PageContainer';
import TextField from 'components/FormElements/AppTextInput';
import AlertDialog from 'components/AlertDialog';
import { useDeleteCompany } from 'hooks/data/company/useCompany';
import { FirmSelector } from 'components/FirmSelector';
import TrancheSelector from 'components/TrancheSelector';
import { COMPANY_ADD } from 'adminConstants/routes';
import { useDisclosure, useFilters, usePagination } from 'further-ui/hooks';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { useGetRole } from 'hooks/ui/useGetRole';
import { useDebounce } from 'usehooks-ts';
import { useNotification } from 'hooks/ui/useNotification';
import {
  generateCollapsedTableColumns,
  generateMainTableColumns,
} from './tableConfig';
import useGroupedCompanies from 'hooks/data/company/useGroupedCompanies';
import PageContent from 'components/PageContent';
import PageFilters, { Filter } from 'components/PageFilters';
import { useBackgroundExports } from 'hooks/data/backgroundExports/useBackgroundExports';
import { BackgroundExportTypes } from 'further-types/background-exports';
import { SortOrder } from 'further-types/utils';

const useStyles = makeStyles()(() => ({
  dFlex: {
    display: 'flex',
  },
  childRowContainer: {
    marginBottom: '1rem',
    boxShadow: '0px 0px 2px rgba(0, 0, 0, 0.1)',
    padding: '20px',
    borderRadius: '4px',
  },
  cmTableDarkHeading: {
    '& th': {
      backgroundColor: '#f0f0f0',
    },
  },
}));

const breadcrumbs = [
  { label: 'Dashboard', link: '/' },
  { label: 'Companies', isActive: true },
];

type CompanyFilters = {
  firmId?: string;
  companyName?: string;
  fundId?: string;
};

const Company: React.FC = () => {
  const history = useHistory();
  const { classes } = useStyles();
  const deleteDisclosure = useDisclosure();
  const deleteCompany = useDeleteCompany();
  const { error } = useNotification();
  const { firmId, isSuperAdmin } = useGetRole();
  const [createPermission, deletePermission, updatePermission] =
    useHasPermissions([
      RolePermission.CreateCompanies,
      RolePermission.DeleteCompanies,
      RolePermission.EditCompanies,
    ]);

  const pagination = usePagination({
    id: 'companies-list-table',
    order: SortOrder.Asc,
    orderBy: 'legalName',
  });

  const defaultFilters: CompanyFilters = {
    firmId,
    companyName: undefined,
    fundId: undefined,
  };

  const { filters, handleChangeFilters } = useFilters<CompanyFilters>(
    'companies-table',
    {
      defaultFilters,
      onFiltersChange: pagination.toFirstPage,
    },
  );

  const debouncedCompanyName = useDebounce(filters.companyName, 150);

  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const fundIdParam = qs.get('fundId');
    if (fundIdParam) {
      handleChangeFilters({
        fundId: fundIdParam,
        firmId: isSuperAdmin ? undefined : firmId,
        companyName: undefined,
      });
    }
  }, []);

  const {
    data: groupedCompanies,
    isFetching: isFetchingGroupedCompanies,
    refetch: refetchGroupedCompanies,
  } = useGroupedCompanies({
    firmId: filters.firmId,
    fundId: filters.fundId,
    page: pagination.page.toString(),
    perPage: pagination.rowsPerPage.toString(),
    type: pagination.order,
    orderBy: pagination.orderBy,
    companyName: debouncedCompanyName || undefined,
  });

  const columns = generateMainTableColumns({
    classes,
    isSuperAdmin,
  });

  const collapsedTableColumns = generateCollapsedTableColumns({
    classes,
    updatePermission,
    deletePermission,
    createPermission,
    history,
    deleteDisclosure,
  });

  const handleConfirmDelete = async () => {
    const companiesList = groupedCompanies?.result;
    const record = deleteDisclosure.actionProps;

    try {
      await deleteCompany.mutateAsync(record._id);

      if (companiesList?.length === 1 && pagination.page > 1) {
        pagination.pageBack();
      }
    } catch (err: any) {
      error(err.response?.data?.responseMsg ?? '');
      refetchGroupedCompanies();
    }
    deleteDisclosure.onClose();
  };

  const { createBackgroundExport, isExporting } = useBackgroundExports();

  const handleExport = () => {
    createBackgroundExport.mutate({
      exportType: BackgroundExportTypes.CompaniesExport,
      firmId: filters.firmId,
      params: {
        companyName: filters.companyName ?? undefined,
        fundId: filters.fundId,
      },
    });
  };

  return (
    <PageContainer heading="Companies" breadcrumbs={breadcrumbs}>
      <PageContent>
        <PageFilters
          pageId="companies"
          buttons={[
            {
              link: COMPANY_ADD,
              label: 'Add Company',
              visible: createPermission,
            },
            {
              label: 'Export',
              onClick: handleExport,
              loading: isExporting,
              disabled: !groupedCompanies?.result?.length,
            },
          ]}
        >
          {isSuperAdmin && (
            <Filter>
              <FirmSelector
                firmId={filters.firmId}
                placeholder="Search by firm"
                onChange={(firmId) => {
                  handleChangeFilters({ firmId: firmId || undefined });
                }}
              />
            </Filter>
          )}
          <Filter>
            <TrancheSelector
              firmId={filters.firmId}
              selectedTranche={
                filters.fundId ? { _id: filters.fundId } : undefined
              }
              clearable
              onChange={(tranche) => {
                handleChangeFilters({ fundId: tranche?._id || undefined });
              }}
            />
          </Filter>
          <Filter>
            <TextField
              type="search"
              name="companyName"
              placeholder="Search by company name"
              fullWidth
              value={filters.companyName}
              onChange={(event) => {
                handleChangeFilters({
                  companyName: event.target.value || undefined,
                });
              }}
            />
          </Filter>
        </PageFilters>
        <Table
          columns={columns}
          onRequestSort={(_, orderBy) => {
            pagination.handleRequestSort(pagination.order, orderBy);
          }}
          order={pagination.order}
          orderBy={pagination.orderBy}
          tablebody={groupedCompanies?.result ?? []}
          onPageChange={pagination.handleChangePage}
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          count={groupedCompanies?.total}
          pagination={true}
          loading={isFetchingGroupedCompanies}
          variant="nohover"
          getRowId={(row) => row._id.toString()}
          collapsedContent={(row) => {
            return (
              <div
                className={`${classes.childRowContainer} ${classes.cmTableDarkHeading}`}
              >
                <Table
                  getRowId={(row) => row.id?.toString() || ''}
                  columns={collapsedTableColumns}
                  tablebody={row.investees || []}
                />
              </div>
            );
          }}
        />
        <AlertDialog
          open={deleteDisclosure.isOpen}
          onClose={deleteDisclosure.onClose}
          onConfirm={handleConfirmDelete}
          title={`Are you sure you wish to delete this company?`}
          content={
            <p>
              This cannot be undone and all information relating to the company
              will be lost.
            </p>
          }
          btnLabels={{
            cancel: 'Go back',
            confirm: 'Delete company',
          }}
        />
      </PageContent>
    </PageContainer>
  );
};

export default Company;
